import React, { useEffect, useState } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Avatar, Button, Divider, Grid, IconButton, InputLabel, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DefaultUploadFile from "../../../components/default-upload-file";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { IFotoObraResponse } from "../../../../model/admin-portal/response/foto-obra-response";
import { IPendingFotoObra } from "../context/obras-reducer";

interface ObraFotosSectionProps {
    idObra?: number;
    pendingFotos?: IPendingFotoObra[];
    onAddPending?: (foto: IPendingFotoObra) => void;
    onRemovePending?: (tempId: string) => void;
}

const emptyDraft: { id: number | undefined; descricao: string; dtaPublicacao: string } = {
    id: undefined, descricao: '', dtaPublicacao: ''
};

// Fotos da Obra: quando a Obra já existe (tem Id), cada foto é enviada na hora
// (upload real de arquivo). Quando a Obra ainda não foi salva, a foto fica
// "pendente" em memória (pendingFotos) e só é enviada quando o "Salvar" da
// Obra criar o registro e a gente souber o Id de verdade.
export const ObraFotosSection = ({ idObra, pendingFotos, onAddPending, onRemovePending }: ObraFotosSectionProps) => {
    const { showMessage, showBackDrop } = useBaseLayoutController();
    const [fotos, setFotos] = useState<IFotoObraResponse[]>([]);
    const [draft, setDraft] = useState(emptyDraft);
    const [file, setFile] = useState<File | null>(null);

    const isStaging = idObra === undefined || idObra === null;

    const loadFotos = async () => {
        if (isStaging) return;
        try {
            const result = await AdminPortalAppService.listFotosObra(idObra);
            setFotos(result || []);
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar fotos da obra' });
        }
    };

    useEffect(() => {
        (async () => { await loadFotos(); })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idObra]);

    const handleEdit = (foto: IFotoObraResponse) => {
        setDraft({ id: foto.id, descricao: foto.descricao, dtaPublicacao: foto.dtaPublicacao });
        setFile(null);
    };

    const handleCancel = () => {
        setDraft(emptyDraft);
        setFile(null);
    };

    const addFoto = async (draftToSave: typeof draft, fileToSave: File | null) => {
        if (isStaging) {
            onAddPending?.({
                tempId: `${Date.now()}-${Math.random()}`,
                descricao: draftToSave.descricao,
                dtaPublicacao: draftToSave.dtaPublicacao,
                file: fileToSave as File
            });
            handleCancel();
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateFotoObra(
                { id: draftToSave.id, idObra, descricao: draftToSave.descricao, dtaPublicacao: draftToSave.dtaPublicacao },
                fileToSave
            );
            showMessage({ type: 'success', message: 'Foto adicionada com sucesso' });
            handleCancel();
            await loadFotos();
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar foto' });
        } finally {
            showBackDrop(false);
        }
    };

    const handleFileSelected = (selected: File | null) => {
        setFile(selected);
        if (!selected) return;

        if (draft.id === undefined) {
            // Fluxo igual ao de Banners: assim que o arquivo é selecionado, já entra na lista - sem clique extra
            (async () => await addFoto(draft, selected))();
        }
    };

    const handleSave = async () => {
        if (!draft.descricao || !draft.dtaPublicacao || isStaging) return;

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateFotoObra(
                { id: draft.id, idObra, descricao: draft.descricao, dtaPublicacao: draft.dtaPublicacao },
                file
            );
            showMessage({ type: 'success', message: 'Foto atualizada com sucesso' });
            handleCancel();
            await loadFotos();
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar foto' });
        } finally {
            showBackDrop(false);
        }
    };

    return (
        <Grid item lg={12} sm={12} xl={12}>
            <Typography variant="subtitle1" fontWeight="bold">Fotos da Obra</Typography>
            <Divider sx={{ mb: 1 }} />

            {isStaging && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    As fotos ficam pendentes e são enviadas automaticamente ao salvar a obra.
                </Typography>
            )}

            {!isStaging && fotos.length > 0 && (
                <List dense>
                    {fotos.map((foto) => (
                        <ListItem
                            key={foto.id}
                            divider
                            secondaryAction={
                                <IconButton size="small" onClick={() => handleEdit(foto)}><EditIcon fontSize="small" /></IconButton>
                            }
                        >
                            <ListItemAvatar>
                                <Avatar variant="rounded" src={foto.imageUrl} sx={{ width: 56, height: 56, mr: 2 }} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={foto.descricao}
                                secondary={foto.dtaPublicacao ? new Date(foto.dtaPublicacao).toLocaleDateString('pt-BR') : ''}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {isStaging && pendingFotos && pendingFotos.length > 0 && (
                <List dense>
                    {pendingFotos.map((foto) => (
                        <ListItem
                            key={foto.tempId}
                            divider
                            secondaryAction={
                                <IconButton size="small" onClick={() => onRemovePending?.(foto.tempId)}><DeleteIcon fontSize="small" /></IconButton>
                            }
                        >
                            <ListItemText
                                primary={foto.descricao}
                                secondary={`${foto.file.name}${foto.dtaPublicacao ? ' - ' + new Date(foto.dtaPublicacao).toLocaleDateString('pt-BR') : ''}`}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>Descrição *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            fullWidth
                            maxLength={100}
                            error={false}
                            value={draft.descricao}
                            onChange={(e) => setDraft({ ...draft, descricao: e.target.value })}
                        />
                    </Grid>
                    <Grid item lg={3} sm={3} xl={3}>
                        <InputLabel>Data de Publicação *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: false } }}
                            value={draft.dtaPublicacao ? dayjs(draft.dtaPublicacao) : null}
                            onChange={(value: Dayjs | null) => setDraft({ ...draft, dtaPublicacao: value?.toISOString() || '' })}
                        />
                    </Grid>
                    <Grid item lg={7} sm={7} xl={7}>
                        <InputLabel>Imagem{draft.id === undefined ? ' *' : ''}</InputLabel>
                        <DefaultUploadFile
                            id="input-foto-obra"
                            file={file}
                            setFileUpload={handleFileSelected}
                            extensions={["jpg", "jpeg", "png"]}
                            maxFileSizeMb={10}
                            error={draft.id === undefined && !file}
                            disabled={draft.id === undefined && (!draft.descricao || !draft.dtaPublicacao)}
                        />
                        {draft.id === undefined && (!draft.descricao || !draft.dtaPublicacao) && (
                            <Typography variant="caption" color="text.secondary">Preencha Descrição e Data antes de selecionar a imagem</Typography>
                        )}
                    </Grid>
                    <Grid item lg={2} sm={12} xl={2} sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                        {draft.id !== undefined && (
                            <React.Fragment>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleSave}
                                    disabled={!draft.descricao || !draft.dtaPublicacao}
                                >
                                    Salvar
                                </Button>
                                <Button size="small" onClick={handleCancel}>Cancelar</Button>
                            </React.Fragment>
                        )}
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Grid>
    );
};
